/**
 * Copyright 2019-2026 ForgeRock AS. All Rights Reserved
 *
 * Use of this code requires a commercial software license with ForgeRock AS
 * or with one of its affiliates. All use shall be exclusively subject
 * to such license between the licensee and ForgeRock AS.
 */

import axios from 'axios';
import { Config } from '@forgerock/javascript-sdk';
import { notify } from '@kyvg/vue3-notification';
import SessionCheck from 'oidcsessioncheck';
import parseSub from '@forgerock/platform-shared/src/utils/OIDC';
import {
  debounce,
  keys,
  pickBy,
} from 'lodash';
import AppAuthHelper from 'appauthhelper/appAuthHelperCompat';
import { generateAmApi } from '@forgerock/platform-shared/src/api/BaseApi';
import { getManagedResource } from '@forgerock/platform-shared/src/api/ManagedResourceApi';
import { getUiConfig } from '@forgerock/platform-shared/src/api/ConfigApi';
import getFQDN from '@forgerock/platform-shared/src/utils/getFQDN';
import { setLocales, overrideTranslations } from '@forgerock/platform-shared/src/utils/overrideTranslations';
import { useUserStore } from '@forgerock/platform-shared/src/stores/user';
import { getAllLocales } from '@forgerock/platform-shared/src/utils/locale';
import { JAVASCRIPT_SDK_TIMEOUT } from '@forgerock/platform-shared/src/utils/constants';
import { actionAuthenticationLogin } from '@/api/AuthenticationApi';
import { setRealmUrl, getRealmConfigs } from '@/api/RealmApi';
import { checkForUpperTenantAuthentication, addAppAuthHelperConfigForSecondaryTenant } from '@/utils/promotions';
import { determineAdminPrivileges, setUserPermissions, setRoutePermissions } from '@/utils/adminPrivileges';
import setAdminMenuItems from '@/utils/setAdminMenuItems';
import { isAllowedBrowser } from '@/utils/pendoUtils';
import store from '@/store';
import i18n from '@/i18n';
import router from '@/router';

const HAS_AM_URL = process.env.VUE_APP_AM_URL && process.env.VUE_APP_AM_URL.length > 0;

// Mutation observer to disable inputs on read-only routes
// this is defined here to avoid multiple observers being created
// it is passed to the setRoutePermissions function to be used and updated there as needed
let observer = null; // eslint-disable-line prefer-const

function getUserInfo(sessionId) {
  return new Promise((resolve) => {
    if (HAS_AM_URL) {
      const urlParams = new URLSearchParams(window.location.search);

      // User information should always come from root for Fraas environments
      const realm = store.state.isFraas ? 'root' : localStorage.getItem('originalLoginRealm') || urlParams.get('realm');
      const path = realm.length > 1 ? `realms/root/realms/${realm}/users` : 'users';
      generateAmApi({
        path,
        apiVersion: 'protocol=2.1,resource=1.0',
      }).get(`/${sessionId}`, { withCredentials: true }).then((response) => {
        resolve(response);
      });
    } else {
      resolve({
        data: {
          roles: [],
        },
      });
    }
  });
}

// Router guard to check authenticated routes
export async function routerGuard(to, from, next, pinia) {
  const userStore = useUserStore(pinia);
  // Retrieve user data to check if the user can access this resource and for future role checks
  if (userStore.userId === '') {
    try {
      const authentication = await actionAuthenticationLogin();
      const { data: { authenticationId, authorization: { roles: idmRoles, component } } } = authentication;
      const idmUIConfigRoles = store.state.SharedStore?.uiConfig?.configuration?.roles;
      userStore.userId = authenticationId;
      userStore.authenticationId = authenticationId;
      userStore.idmRoles = idmRoles;
      userStore.idmUIAdminRoles = keys(pickBy(idmUIConfigRoles, (role) => role === 'ui-admin'));

      // If this is an internal managed user then there is no point in making validation
      if (component === 'internal/user') {
        // Initialise user data to generic admin information
        userStore.setInternalUserDetails();

        // only set as amAdmin when HAS_AM_URL
        if (HAS_AM_URL) userStore.amAdmin = true;

        if (to.name === 'Forbidden') {
          next({ path: '/' });
        } else {
          next();
        }
      } else {
        try {
          const results = await axios.all([
            getManagedResource(authentication.data.authorization.component.split('/')[1], authenticationId),
            // using claims.sub as userSearchAttribute in case AM doesn't match UUID
            getUserInfo(userStore.userSearchAttribute),
          ]);
          userStore.setUserDetails(results[0].data);
          userStore.amRoles = results[1].data.roles;

          if (userStore.realmAdmin || userStore.adminUser) {
            if (to.name === 'Forbidden') {
              next({ path: '/' });
            } else {
              setRoutePermissions(to, next, observer);
            }
          } else {
            // if the to object is already forbidden, we have already seen it, and need to redirect to enduser
            // because the user has signed out and tried to sign back in with same creds
            if (to.name === 'Forbidden') {
              window.location.href = process.env.VUE_APP_ENDUSER_URL;
            }
            next({ name: 'Forbidden' });
          }
        } catch {
          notify({
            group: 'AdminMessage',
            type: 'danger',
            text: i18n.global.t('application.errors.failedLogout'),
          });
        }
      }
    } catch {
      // do nothing
    }
  } else {
    setRoutePermissions(to, next, observer);
  }
}

const startApp = (loadApp) => {
  const promises = [
    getUiConfig(),
    HAS_AM_URL ? getRealmConfigs() : null,
  ];

  Promise.all(promises)
    .then((results) => {
      const uiConfig = results[0];
      // Get & set locales
      const { locales } = getAllLocales(uiConfig.data.configuration);
      setLocales(i18n, locales);
      document.getElementsByTagName('html')[0].setAttribute('lang', i18n.global.locale);

      // Set config to store
      store.commit('SharedStore/setUiConfig', uiConfig.data);

      if (results[1]) {
        const realmData = results[1].data;
        // Set realm data
        if (realmData.result[0].name === '/') {
          realmData.result[0].name = 'root';
        }
        store.commit('setRealms', realmData.result);
      }
    })
    .then(() => overrideTranslations(i18n, 'admin'))
    .finally(() => {
      // set the serverConfig with a timeout of 60 seconds this is required because the default timeout is 5 seconds and some trees can take longer to load
      // @see IAM-6840
      Config.set({
        serverConfig: {
          baseUrl: getFQDN(`${process.env.VUE_APP_AM_URL}/`),
          timeout: JAVASCRIPT_SDK_TIMEOUT,
        },
      });
      loadApp();
      if (isAllowedBrowser()) {
        setTimeout(() => {
          store.commit('setLoadPendo', true);
        }, 3000);
      }
    });
};

const addAppAuth = (loadApp, pinia) => {
  const AM_URL = store.state.SharedStore.amBaseURL;
  const urlParams = new URLSearchParams(window.location.search);
  const originalLoginRealm = localStorage.getItem('originalLoginRealm');
  const pageLoadUrlRealm = urlParams.get('realm');

  /**
   * If there is an originalLoginRealm here it's because the page was refreshed while in a sub-realm.
   * In this case we want to set the realm used to build the realmPath below to the originally logged in realm.
   * If we don't do this all the appAuthClient config settings will be different from when the page was orignially
   * logged in which causes REST calls to fail and breaks logout because it tries to logout from the wrong realm.
   * The second option of pageLoadUrlRealm pulls the realm if specified in the url. This is important for realm admin
   * login, as the root realm default does not allow realm-admins to log in to their realm.
   * The third option starts as root within cloud at this point in the flow, and then changes to the current realm.
  */
  const realm = originalLoginRealm || pageLoadUrlRealm || store.state.realm;
  /**
   * If realm is different from store.state.realm here reset the value in the store.
   * Depending on the timing of when this runs the realm in the store may be set to the default value (aka root).
   * We want to make sure the store value set to the correct realm at this point.
   * The store value may be changed later on in processing.
   */
  if (realm && realm !== store.state.realm) {
    store.commit('setRealm', realm);
  }

  let clickSessionHandler;
  let keypressSessionHandler;
  let pageFocusHandler;
  let sessionCheck;
  let removeRouterGuard;
  let realmPath = '';

  // Fraas environments should keep the realm path as root at this point
  if (realm !== '/' && realm !== 'root' && !store.state.isFraas) {
    store.commit('setRealm', realm);

    if (realm.startsWith('/')) {
      realmPath = `realms/root/realms/${realm.substring(1)}/`;
    } else {
      realmPath = `realms/root/realms/${realm}/`;
    }
  }

  const commonSettings = {
    clientId: store.state.idmClientID,
    authorizationEndpoint: process.env.VUE_APP_AUTHORIZATION_ENDPOINT || `${AM_URL}/oauth2/${realmPath}authorize`,
  };

  const resourceServers = {
    [store.state.idmUploadURL]: 'fr:idm:*',
    [store.state.idmExportURL]: 'fr:idm:*',
    [store.state.SharedStore.fraasLoggingKeyURL]: 'openid',
    [store.state.SharedStore.idmBaseURL]: 'fr:idm:*',
    [store.state.SharedStore.analyticsURL]: 'fr:idm:*',
  };

  if (store.state.isFraas) {
    resourceServers[store.state.SharedStore.fraasPromotionUrl] = 'fr:idc:promotion:*';
    resourceServers[`${store.state.SharedStore.fraasEnvironmentUrl}/custom-domains`] = 'fr:idc:custom-domain:*';
    resourceServers[`${store.state.SharedStore.fraasEnvironmentUrl}/certificates`] = 'fr:idc:certificate:*';
    resourceServers[`${store.state.SharedStore.fraasEnvironmentUrl}/csrs`] = 'fr:idc:certificate:*';
    resourceServers[`${store.state.SharedStore.fraasEnvironmentUrl}/secrets`] = 'fr:idm:*';
    resourceServers[`${store.state.SharedStore.fraasEnvironmentUrl}/variables`] = 'fr:idm:*';
    resourceServers[`${store.state.SharedStore.fraasEnvironmentUrl}/count`] = 'fr:idc:esv:*';
    resourceServers[`${store.state.SharedStore.fraasEnvironmentUrl}/sso-cookie`] = 'fr:idc:sso-cookie:*';
    resourceServers[`${store.state.SharedStore.fraasEnvironmentUrl}/proxy-connect`] = 'fr:idc:proxy-connect:*';
    resourceServers[`${store.state.SharedStore.fraasEnvironmentUrl}/mtls`] = 'fr:idc:mtls:*';
    resourceServers[`${store.state.SharedStore.fraasEnvironmentUrl}/cookie-domains`] = 'fr:idc:cookie-domain:*';
    resourceServers[`${store.state.SharedStore.fraasEnvironmentUrl}/startup`] = 'fr:idm:*';
    if (store.state.SharedStore.pingOneIntegrationsEnabled) {
      resourceServers[`${store.state.SharedStore.fraasEnvironmentUrl}/p1integration`] = 'fr:idc:universalservice:* fr:idc:esv:*';
    }
    resourceServers[`${store.state.SharedStore.fraasEnvironmentUrl}/release`] = 'fr:idc:release:*';
    resourceServers[store.state.SharedStore.fraasFederationUrl] = 'fr:idc:federation:*';
    resourceServers[`${store.state.SharedStore.fraasEnvironmentUrl}/content-security-policy`] = 'fr:idc:content-security-policy:*';
    resourceServers[`${store.state.SharedStore.fraasMonitoringURL}/logs`] = 'fr:idc:monitoring:*';
  }

  if (store.state.SharedStore.governanceEnabled) {
    resourceServers[store.state.SharedStore.igaApiUrl] = 'fr:iga:*';
    resourceServers[store.state.SharedStore.igaOrchestrationApiUrl] = 'fr:idc:analytics:*';
  }

  if (store.state.SharedStore.autoAccessEnabled) {
    resourceServers[store.state.SharedStore.autoAccessJasUrl] = 'fr:autoaccess:*';
    resourceServers[store.state.SharedStore.autoAccessApiUrl] = 'fr:autoaccess:*';
  }

  if (store.state.SharedStore.autoReportsEnabled || store.state.SharedStore.autoAccessEnabled) {
    resourceServers[store.state.SharedStore.autoAccessReportsUrl] = 'fr:idc:analytics:*';
  }

  if (store.state.SharedStore.wsfedEnabled) {
    resourceServers[store.state.SharedStore.pingFederateUrl] = 'fr:idc:ws:admin';
  }

  if (store.state.SharedStore.journeyAIEnabled) {
    // HELIX Resource endpoints
    resourceServers[`${store.state.SharedStore.helixEnvironmentUrl}/environments/${store.state.SharedStore.helixEnvironmentId}/agents`] = 'fr:helix:*';
    resourceServers[`${store.state.SharedStore.helixEnvironmentUrl}/environments/${store.state.SharedStore.helixEnvironmentId}/conversations`] = 'fr:helix:*';
  }

  AppAuthHelper.init({
    clientId: commonSettings.clientId,
    authorizationEndpoint: commonSettings.authorizationEndpoint,
    tokenEndpoint: process.env.VUE_APP_TOKEN_ENDPOINT || `${AM_URL}/oauth2/${realmPath}access_token`,
    revocationEndpoint: process.env.VUE_APP_REVOCATION_ENDPOINT || `${AM_URL}/oauth2/${realmPath}token/revoke`,
    endSessionEndpoint: process.env.VUE_APP_END_SESSION_ENDPOINT || `${AM_URL}/oauth2/${realmPath}connect/endSession`,
    identityProxyPreference: 'XHR',
    resourceServers,
    tokensAvailableHandler: (claims) => {
      const sub = parseSub(claims);
      const userStore = useUserStore(pinia);
      userStore.userSearchAttribute = sub;

      if (sub !== 'amadmin') {
        determineAdminPrivileges(sub).then((adminPrivileges) => {
          userStore.privileges = adminPrivileges;
          if (store.state.isFraas && !userStore.hasTenantAdminPrivilege && !userStore.hasFederationAdminPrivilege) {
            // this is a fraas deployment and the user is not a super admin or tenant admin so we need to set
            // the user's permissions based on defined ui policies
            setUserPermissions().then(() => {
              setAdminMenuItems();
            });
          } else {
            setAdminMenuItems();
          }
        });
      } else {
        setAdminMenuItems();
      }

      if (sessionCheck) {
        sessionCheck.destroy();
      }

      if (removeRouterGuard) {
        removeRouterGuard();
      }

      sessionCheck = new SessionCheck({
        clientId: commonSettings.clientId,
        opUrl: commonSettings.authorizationEndpoint,
        subject: claims.sub,
        invalidSessionHandler() {
          window.logout(false);
        },
        sessionClaimsHandler(newClaims) {
          if (claims.realm !== newClaims.realm) {
            this.invalidSessionHandler();
          }

          /**
           * Check that the originalLoginRealm session variable is set.
           * If not set it so we know what realm to use for logout.
          */
          if (!localStorage.getItem('originalLoginRealm')) {
            // if the realm comes in as '/' save it as 'root' unless isFraas -
            // no access to the root for isFraas
            let originalRealm = realm === '/' ? 'root' : realm;
            if (originalRealm === 'root' && store.state.isFraas) {
              originalRealm = store.state.fraasDefaultRealm;
            }
            localStorage.setItem('originalLoginRealm', originalRealm);
          }
        },
        cooldownPeriod: 5,
      });
      const triggerSession = () => {
        sessionCheck.triggerSessionCheck();
      };
      /**
       * If the originalLoginRealm differs from the realm from the initial load of the page it's
       * because the page was refreshed while in a subrealm. In this case set the app's realm back to
       * the one signified in the url params.
       */
      if (originalLoginRealm && pageLoadUrlRealm && originalLoginRealm !== pageLoadUrlRealm) {
        setRealmUrl(pageLoadUrlRealm);
        store.commit('setRealm', pageLoadUrlRealm);
      }
      // When isFraas never allow access to the root realm
      if (store.state.isFraas && (realm === '/' || realm === 'root' || realm === '/root')) {
        // if the pageLoadUrlRealm is '/' or 'root' use the default fraas realm otherwise use the one passed in via the url
        const fraasRealm = (!pageLoadUrlRealm || pageLoadUrlRealm === '/' || pageLoadUrlRealm === 'root' || pageLoadUrlRealm === '/root') ? store.state.fraasDefaultRealm : pageLoadUrlRealm;
        setRealmUrl(fraasRealm);
        store.commit('setRealm', fraasRealm);
      }

      // check the validity of the session immediately
      triggerSession();

      if (clickSessionHandler) {
        document.removeEventListener('click', clickSessionHandler);
      }
      if (keypressSessionHandler) {
        document.removeEventListener('keypress', keypressSessionHandler);
      }
      if (pageFocusHandler) {
        document.removeEventListener('focusin', pageFocusHandler);
      }

      // Define event handlers
      clickSessionHandler = debounce(triggerSession, 100);
      keypressSessionHandler = debounce(triggerSession, 100);
      pageFocusHandler = debounce(triggerSession, 100);

      // check with every route change thru router
      removeRouterGuard = router.beforeEach((to, from, next) => {
        triggerSession();
        next();
      });

      // Ensure we have updated event capturing
      document.addEventListener('click', clickSessionHandler);
      document.addEventListener('keypress', keypressSessionHandler);
      document.addEventListener('focusin', pageFocusHandler);

      if (store.state.SharedStore.fraasPromotionUrl) {
        // If promotions are allowed on tenant, check for authentication tokens
        // for the upper tenant. If they exist, we need to reinitialise
        // appAuthHelper for the upper tenant
        checkForUpperTenantAuthentication().then((tokensExist) => {
          if (tokensExist) {
            // Tokens for upper tenant exist, attempt to initiate appAuthHelper
            addAppAuthHelperConfigForSecondaryTenant(store.state.SharedStore.fraasPromotionEgressUrl, () => startApp(loadApp));
          } else {
            // Continue without initialising appAuthHelper for upper tenant
            startApp(loadApp);
          }
        });
      } else {
        startApp(loadApp);
      }
    },
  }).then(
    // In this application, we want tokens immediately, before any user interaction is attempted
    () => AppAuthHelper.getTokens(),
  );

  // trigger logout from anywhere in the SPA by calling this global function
  window.logout = (clearHash = true) => {
    const loginRealm = localStorage.getItem('originalLoginRealm');
    /**
     * If there is an originalLoginRealm and that realm is different from the current realm
     * we need to set store.state.realm to it's original state so we can log out properly.
     */
    if (loginRealm) {
      if (store.state.realm !== loginRealm) {
        setRealmUrl(loginRealm);
        store.commit('setRealm', loginRealm);
      }
      localStorage.removeItem('originalLoginRealm');
    }

    // stop the router from watching hash changes so no ugly redirects happen whilst logging out
    router.listening = false;

    // clear hash so user is not directed to previous hash on subsequent logins
    if (clearHash) window.location.hash = '';

    // This will also logout of the upper tenant if the user has authenticated there
    AppAuthHelper.logout().then(() => window.location.reload());
  };
};

export default function initialize(loadApp, pinia) {
  store.commit('setEnvironment', process.env);
  store.commit('SharedStore/setBaseURLs', process.env);
  store.commit('SharedStore/setCurrentPackage', 'admin');
  store.commit('SharedStore/setFeatureFlags', process.env);

  // Import the routes js file. The route file defaults to routes.platform.js
  // but can be overridden using the VUE_APP_ROUTES_FILE environment
  // variable. The file extension is included here so webpack can anticipate
  // which files to include.
  try {
    const routePath = store.state.routesFile;
    const defaultRoutes = require(`../../../config/routes/${routePath}.js`); // eslint-disable-line
    // TODO: Make separate autoaccess routes file so the component imports don't actually work if there is no auto access enabled?
    // If workforce is disabled, use the OAuth Clients view instead of Applications- but keep the same url
    if (!store.state.SharedStore.workforceEnabled) {
      const appRoute = defaultRoutes.default.find((route) => route.path === '/applications').children[0]; // the first child route is Applications
      appRoute.component = () => import('@/views/Applications/ListOAuthClients');
    }
    defaultRoutes.default.forEach(router.addRoute);
  } catch (error) {
    console.error(i18n.global.t('application.errors.errorLoadingRoutes'), error); // eslint-disable-line
  }

  addAppAuth(loadApp, pinia);
}
